
import { RefreshCcw, Lock, Clock, Power, Send, Wifi } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CommandHistoryTable } from "./CommandHistoryTable";
import { useDeviceCommands } from "@/hooks/useDeviceCommands";

interface Device {
  id: string;
  name: string;
  user: string;
  os: string;
  version: string;
  status: "active" | "prepared" | "inactive";
  lastSync: string;
  group: string;
}

interface DeviceCommandsContentProps {
  device: Device;
  activeSection: string;
}

interface CommandMessage {
  type: "success" | "error" | "pending";
  text: string;
}

export const DeviceCommandsContent = ({ device, activeSection }: DeviceCommandsContentProps) => {
  const [successfulCommands, setSuccessfulCommands] = useState<Set<string>>(new Set());
  const [currentMessage, setCurrentMessage] = useState<CommandMessage | null>(null);
  const { executeCommand, isLoading } = useDeviceCommands();
  
  // Command handlers
  const handleExecuteCommand = async (commandName: string) => {
    const isActive = device.status === "active";
    
    if (!isActive) {
      setCurrentMessage({
        type: "error",
        text: `Device ${device.name} is not active. Commands can only be executed on active devices.`
      });
      setTimeout(() => setCurrentMessage(null), 3000);
      return;
    }

    // Show pending message immediately
    setCurrentMessage({
      type: "pending",
      text: `Sending ${commandName} command to ${device.name}...`
    });

    try {
      const result = await executeCommand(device.id, commandName);
      
      if (result.success) {
        setSuccessfulCommands(prev => new Set(prev).add(commandName));
        setCurrentMessage({
          type: "success",
          text: `${commandName} command sent successfully to ${device.name}`
        });
      } else {
        setCurrentMessage({
          type: "error",
          text: `Failed to execute ${commandName} on ${device.name}. ${result.error || 'Please try again.'}`
        });
      }
    } catch (error) {
      setCurrentMessage({
        type: "error",
        text: `Failed to execute ${commandName} on ${device.name}. Please try again.`
      });
    }
    
    // Clear message after 3 seconds
    setTimeout(() => setCurrentMessage(null), 3000);
  };

  const getButtonVariant = (commandName: string) => {
    return successfulCommands.has(commandName) ? "default" : "outline";
  };

  const getButtonClasses = (commandName: string) => {
    const baseClasses = "h-auto py-1.5 px-2 flex items-center justify-start space-x-1.5 text-xs border-border hover:bg-accent hover:text-accent-foreground min-w-0";
    const successClasses = "bg-green-500 hover:bg-green-600 text-white border-green-500";
    
    return successfulCommands.has(commandName) ? `${baseClasses} ${successClasses}` : baseClasses;
  };

  return (
    <div className="w-full space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-3">Management Commands</h2>
          
        {device.status !== "active" && (
          <Alert className="mb-3">
            <AlertTitle className="text-sm">Device Not Active</AlertTitle>
            <AlertDescription className="text-xs">
              This device is currently {device.status}. Commands can only be executed on active devices.
            </AlertDescription>
          </Alert>
        )}

        {currentMessage && (
          <div className="mb-3">
            <Alert className={`${
              currentMessage.type === "success" ? "border-green-500 bg-green-50 text-green-800" :
              currentMessage.type === "error" ? "border-red-500 bg-red-50 text-red-800" :
              "border-blue-500 bg-blue-50 text-blue-800"
            }`}>
              <AlertDescription className="text-xs">{currentMessage.text}</AlertDescription>
            </Alert>
          </div>
        )}
          
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5">
          <Button 
            onClick={() => handleExecuteCommand("Update Inventory")}
            variant={getButtonVariant("Update Inventory")}
            size="sm"
            className={getButtonClasses("Update Inventory")}
            disabled={isLoading}
          >
            <RefreshCcw className="h-3 w-3 flex-shrink-0 text-blue-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Update Inventory</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Clear Passcode")}
            variant={getButtonVariant("Clear Passcode")}
            size="sm"
            className={getButtonClasses("Clear Passcode")}
            disabled={isLoading}
          >
            <Lock className="h-3 w-3 flex-shrink-0 text-red-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Clear Passcode</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Clear Screen Time Passcode")}
            variant={getButtonVariant("Clear Screen Time Passcode")}
            size="sm"
            className={getButtonClasses("Clear Screen Time Passcode")}
            disabled={isLoading}
          >
            <Clock className="h-3 w-3 flex-shrink-0 text-orange-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Clear Screen Time Passcode</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Restart Device")}
            variant={getButtonVariant("Restart Device")}
            size="sm"
            className={getButtonClasses("Restart Device")}
            disabled={isLoading}
          >
            <Power className="h-3 w-3 flex-shrink-0 text-red-600" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Restart Device</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Send Blank Push")}
            variant={getButtonVariant("Send Blank Push")}
            size="sm"
            className={getButtonClasses("Send Blank Push")}
            disabled={isLoading}
          >
            <Send className="h-3 w-3 flex-shrink-0 text-green-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Send Blank Push</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Enable Hotspot")}
            variant={getButtonVariant("Enable Hotspot")}
            size="sm"
            className={getButtonClasses("Enable Hotspot")}
            disabled={isLoading}
          >
            <Wifi className="h-3 w-3 flex-shrink-0 text-purple-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Enable Hotspot</span>
          </Button>

          <Button 
            onClick={() => handleExecuteCommand("Disable Hotspot")}
            variant={getButtonVariant("Disable Hotspot")}
            size="sm"
            className={getButtonClasses("Disable Hotspot")}
            disabled={isLoading}
          >
            <Wifi className="h-3 w-3 flex-shrink-0 text-gray-500" />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-xs">Disable Hotspot</span>
          </Button>
        </div>
      </div>

      <CommandHistoryTable deviceId={device.id} />
    </div>
  );
};
